/**
 * Form input components with StyleX styling.
 */
import * as stylex from "@stylexjs/stylex";

import { colors, fontSize, radius, space } from "../tokens.stylex";

const styles = stylex.create({
  group: {
    marginBottom: space.md,
  },
  groupLast: {
    marginBottom: space.lg,
  },
  label: {
    display: "block",
    marginBottom: space.xs,
    fontSize: fontSize.sm,
    color: colors.foregroundMuted,
  },
  input: {
    width: "100%",
    borderRadius: radius.md,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.borderSubtle,
    backgroundColor: colors.input,
    paddingInline: space.md,
    paddingBlock: space.sm,
    color: colors.inputForeground,
    fontSize: fontSize.sm,
    ":focus": {
      outline: "none",
      borderColor: colors.ring,
    },
  },
  select: {
    borderRadius: radius.md,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.borderSubtle,
    backgroundColor: colors.input,
    paddingInline: space.sm,
    paddingBlock: space.xs,
    color: colors.inputForeground,
    fontSize: fontSize.sm,
  },
  error: {
    marginTop: space.xs,
    fontSize: fontSize.sm,
    color: colors.destructive,
  },
  space: {
    display: "flex",
    flexDirection: "column",
    gap: space.md,
  },
});

// Export the styles for direct usage with stylex.props()
export const form = styles;

interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "style"> {
  xstyle?: stylex.StyleXStyles;
}

function Input({ xstyle, ...props }: InputProps) {
  return <input {...stylex.props(styles.input, xstyle)} {...props} />;
}

interface LabelProps
  extends Omit<React.LabelHTMLAttributes<HTMLLabelElement>, "style"> {
  xstyle?: stylex.StyleXStyles;
}

function Label({ xstyle, ...props }: LabelProps) {
  // biome-ignore lint/a11y/noLabelWithoutControl: htmlFor is passed via props
  return <label {...stylex.props(styles.label, xstyle)} {...props} />;
}

interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "style"> {
  xstyle?: stylex.StyleXStyles;
}

function Select({ xstyle, ...props }: SelectProps) {
  return <select {...stylex.props(styles.select, xstyle)} {...props} />;
}

interface FieldErrorProps {
  children: React.ReactNode;
  xstyle?: stylex.StyleXStyles;
}

function FieldError({ children, xstyle }: FieldErrorProps) {
  return <p {...stylex.props(styles.error, xstyle)}>{children}</p>;
}

export {
  Input,
  Label,
  Select,
  FieldError,
  type InputProps,
  type LabelProps,
  type SelectProps,
  type FieldErrorProps,
};
